import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useNavScroll } from '../../hooks/useNavScroll';
import { useModal } from '../../contexts/ModalContext';

const navLinks = [
  { label: 'Home', path: '/' },
  { label: 'Services', path: '/services' },
  { label: 'About', path: '/about' },
  { label: 'Location', path: '/location' },
];

export default function Navbar() {
  const scrolled = useNavScroll(80);
  const [menuOpen, setMenuOpen] = useState(false);
  const { openModal } = useModal();
  const location = useLocation();

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-600 ${
        scrolled
          ? 'bg-soft-white/97 backdrop-blur-md shadow-[0_1px_0_0_#E5DFD5]'
          : 'bg-transparent'
      }`}
    >
      <nav className="flex items-center justify-between px-8 lg:px-16 h-[72px]">
        {/* Logo */}
        <Link to="/" className="flex items-center cursor-pointer" aria-label="Talo Architect Home">
          <img
            src="https://storage.readdy-site.link/project_files/dee54ef1-5999-4bcb-b321-ea9937ee8043/1be9aad7-eb01-4664-a9b0-4bd3de2c6806_talo-logo.png?v=a7ee781370f881c08b811bb6b39ede89"
            alt="Talo Architect, P.C."
            className={`h-10 w-auto object-contain transition-all duration-500 ${
              scrolled ? 'brightness-0' : 'brightness-0 invert'
            }`}
          />
        </Link>

        {/* Desktop Nav */}
        <ul className="hidden lg:flex items-center gap-8">
          {navLinks.map((link) => (
            <li key={link.path}>
              <Link
                to={link.path}
                className={`font-sans text-[11px] tracking-[0.18em] uppercase transition-colors duration-300 cursor-pointer whitespace-nowrap relative group ${
                  location.pathname === link.path
                    ? scrolled
                      ? 'text-dark-ink'
                      : 'text-white'
                    : scrolled
                    ? 'text-warm-gray hover:text-dark-ink'
                    : 'text-white/70 hover:text-white'
                }`}
              >
                {link.label}
                <span className={`absolute -bottom-1 left-0 right-0 h-px transition-all duration-300 ${
                  location.pathname === link.path
                    ? scrolled ? 'bg-wood scale-x-100' : 'bg-white/50 scale-x-100'
                    : 'bg-wood scale-x-0 group-hover:scale-x-100'
                }`} />
              </Link>
            </li>
          ))}
        </ul>

        {/* Right Side */}
        <div className="hidden lg:flex items-center gap-5">
          <a
            href="tel:+19146452940"
            className={`font-sans text-[11px] tracking-wide transition-colors duration-300 whitespace-nowrap cursor-pointer ${
              scrolled ? 'text-warm-gray hover:text-dark-ink' : 'text-white/70 hover:text-white'
            }`}
          >
            (914) 645-2940
          </a>
          <button
            onClick={openModal}
            className={`font-sans text-[10px] tracking-[0.18em] uppercase px-5 py-2.5 transition-all duration-300 cursor-pointer whitespace-nowrap ${
              scrolled
                ? 'bg-dark-ink text-soft-white hover:bg-wood'
                : 'bg-white/12 text-white border border-white/30 hover:bg-white/22'
            }`}
          >
            Schedule Consultation
          </button>
        </div>

        {/* Mobile Menu Button */}
        <button
          className={`lg:hidden transition-colors duration-300 cursor-pointer w-9 h-9 flex items-center justify-center ${
            scrolled ? 'text-dark-ink' : 'text-white'
          }`}
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          <i className={`ri-${menuOpen ? 'close' : 'menu-3'}-line text-xl`} />
        </button>
      </nav>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="lg:hidden bg-soft-white border-t border-stone px-8 py-8">
          <ul className="flex flex-col gap-5 mb-8">
            {navLinks.map((link) => (
              <li key={link.path}>
                <Link
                  to={link.path}
                  onClick={() => setMenuOpen(false)}
                  className="font-sans text-sm tracking-[0.15em] uppercase text-warm-gray hover:text-dark-ink transition-colors cursor-pointer"
                >
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>
          <a href="tel:+19146452940" className="block font-sans text-sm text-dark-ink mb-5 cursor-pointer">
            (914) 645-2940
          </a>
          <button
            onClick={() => { openModal(); setMenuOpen(false); }}
            className="w-full bg-dark-ink text-soft-white font-sans text-[10px] tracking-[0.18em] uppercase py-4 cursor-pointer whitespace-nowrap hover:bg-wood transition-colors"
          >
            Schedule Consultation
          </button>
        </div>
      )}
    </header>
  );
}
